import { Button, Checkbox, DataTable, Dialog, Input, InputGroup, Tooltip } from "@/components/ui";
import { Page, PaginationParams } from "@/services/bedrock";
import { useDebounced } from "@/utils";
import type { SortingState } from "@tanstack/react-table";
import { ColumnDef } from "@tanstack/react-table";
import axios from "axios";
import { ArrowDown, ArrowUp, ArrowUpDown, SearchIcon } from "lucide-react";
import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 350;

export type FetchPageParams = PaginationParams & {
  search: string | undefined;
  sort: { id: string; desc: boolean } | undefined;
};

type CommonProps<TRecord> = {
  open: boolean;
  title?: string;
  description?: string;
  searchPlaceholder?: string;
  onOpenChange: (open: boolean) => void;
  defaultSelectedItems?: TRecord[];
};

export type BaseRecordSelectionDialogProps<TRecord> =
  | (CommonProps<TRecord> & {
      multiSelect?: false;
      onSelect: (item: TRecord) => void;
    })
  | (CommonProps<TRecord> & {
      multiSelect: true;
      onSelect: (items: TRecord[]) => void;
    });

export type RecordSelectionDialogProps<TRecord> = BaseRecordSelectionDialogProps<TRecord> & {
  columns: ColumnDef<TRecord, unknown>[];
  fetchPage: (params: FetchPageParams, signal: AbortSignal) => Promise<Page<TRecord>>;
  getItemId?: (item: TRecord) => string | number;
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
};

export function createSortableHeader(label: string) {
  return function SortableHeader(context: {
    column: {
      getToggleSortingHandler: () => ((e: unknown) => void) | undefined;
      getIsSorted: () => false | "asc" | "desc";
    };
  }) {
    const { column } = context;
    const sorted = column.getIsSorted();
    const handler = column.getToggleSortingHandler();
    return (
      <Button type="button" variant="ghost" className="-ml-4 h-8 font-medium hover:bg-muted/50" onClick={handler}>
        {label}
        {sorted === "asc" ? (
          <ArrowUp className="ml-1 size-4" />
        ) : sorted === "desc" ? (
          <ArrowDown className="ml-1 size-4" />
        ) : (
          <ArrowUpDown className="ml-1 size-4 opacity-50" />
        )}
      </Button>
    );
  };
}

function sortingKey(s: SortingState): string {
  const first = s[0];
  return first ? `${first.id}:${first.desc}` : "";
}

function TruncatedCell({ cell }: { cell: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const hasOverflow = element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth;
    setIsTruncated(hasOverflow);
  }, [cell]);

  const textValue = typeof cell === "string" || typeof cell === "number" ? String(cell) : undefined;

  const content = (
    <div ref={ref} className="overflow-hidden text-ellipsis line-clamp-3">
      {cell}
    </div>
  );

  if (!isTruncated || !textValue) return content;

  return (
    <Tooltip.Provider delay={600}>
      <Tooltip>
        <Tooltip.Trigger render={<div>{content}</div>} />
        <Tooltip.Content className="max-w-md whitespace-pre-wrap break-words">{textValue}</Tooltip.Content>
      </Tooltip>
    </Tooltip.Provider>
  );
}

function RecordSelectionDialogInner<TRecord>({
  open,
  onOpenChange,
  onSelect,
  multiSelect = false,
  title = "Select",
  description = "Search and select from the list.",
  searchPlaceholder = "Search...",
  fetchPage,
  columns,
  getItemId = (item: TRecord) => (item as { id: string | number }).id,
  defaultSelectedItems = [],
  secondaryAction,
}: RecordSelectionDialogProps<TRecord>) {
  const [data, setData] = useState<TRecord[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [pagination, setPagination] = useState<PaginationParams>({
    page: 1,
    perPage: PAGE_SIZE,
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [selectedItems, setSelectedItems] = useState<TRecord[]>(defaultSelectedItems);
  const debouncedSearch = useDebounced(searchInput.trim(), SEARCH_DEBOUNCE_MS, (value, previousValue) => {
    if (value !== previousValue) setPagination(prev => ({ ...prev, page: 1 }));
  });
  const abortControllerRef = useRef<AbortController | null>(null);

  const loadPage = useCallback(() => {
    if (!open) return;

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const sort = sorting[0];
    setLoading(true);
    fetchPage(
      {
        search: debouncedSearch || undefined,
        ...pagination,
        sort: sort ? { id: sort.id, desc: sort.desc } : undefined,
      },
      controller.signal
    )
      .then(result => {
        if (abortControllerRef.current !== controller) return;
        setData(result.content);
        setTotalRecords(result.pagination.totalCount);
        setTotalPages(result.pagination.totalPages);
      })
      .catch(err => {
        if (axios.isCancel(err)) return;
        console.error("Failed to fetch records", err);
      })
      .finally(() => {
        if (abortControllerRef.current === controller) {
          setLoading(false);
          abortControllerRef.current = null;
        }
      });
  }, [open, debouncedSearch, pagination, sorting, fetchPage]);

  useEffect(loadPage, [loadPage]);

  useEffect(() => {
    if (open) {
      setSelectedItems(defaultSelectedItems ?? []);
    } else {
      setSearchInput("");
      setPagination({ page: 1, perPage: PAGE_SIZE });
      setSorting([]);
    }
  }, [open]);

  const prevSortingKey = useRef(sortingKey(sorting));
  useEffect(() => {
    if (!open) return;
    if (prevSortingKey.current !== sortingKey(sorting)) {
      prevSortingKey.current = sortingKey(sorting);
      setPagination(prev => ({ ...prev, page: 1 }));
    }
  }, [open, sorting]);

  const handleSortingChange = useCallback((updaterOrValue: SortingState | ((old: SortingState) => SortingState)) => {
    setSorting(prev => (typeof updaterOrValue === "function" ? updaterOrValue(prev) : updaterOrValue));
  }, []);

  const handleSelect = () => {
    if (selectedItems.length === 0) return;
    if (multiSelect) {
      (onSelect as (items: TRecord[]) => void)(selectedItems);
    } else {
      (onSelect as (item: TRecord) => void)(selectedItems[0]);
    }
  };

  const toggleSelection = useCallback(
    (item: TRecord) => {
      const id = getItemId(item);
      setSelectedItems(prev => {
        const isSelected = prev.some(i => getItemId(i) === id);
        if (multiSelect) {
          if (isSelected) return prev.filter(i => getItemId(i) !== id);
          return [...prev, item];
        }
        return isSelected ? [] : [item];
      });
    },
    [multiSelect, getItemId]
  );

  const visibleItems = useMemo(() => new Set(data.map(p => getItemId(p))), [data, getItemId]);
  const allVisibleSelected = data.length > 0 && data.every(p => selectedItems.includes(p));
  const someVisibleSelected = data.some(p => selectedItems.includes(p));

  const toggleSelectAllVisible = useCallback(() => {
    if (allVisibleSelected) {
      setSelectedItems(prev => {
        return prev.filter(p => !visibleItems.has(getItemId(p)));
      });
    } else {
      setSelectedItems(prev => {
        return [...prev, ...data.filter(p => !selectedItems.includes(p))];
      });
    }
  }, [allVisibleSelected, visibleItems, data, selectedItems]);

  const selectColumn: ColumnDef<TRecord, unknown> = useMemo(
    () => ({
      id: "select",
      header: multiSelect
        ? () => (
            <Checkbox
              checked={allVisibleSelected}
              indeterminate={someVisibleSelected && !allVisibleSelected}
              onCheckedChange={toggleSelectAllVisible}
              aria-label="Select all"
            />
          )
        : "",
      cell: ({ row }) => {
        const record = row.original;
        const id = getItemId(record);
        const isSelected = selectedItems.some(i => getItemId(i) === id);
        return (
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => toggleSelection(record)}
            aria-label={isSelected ? "Deselect" : "Select"}
          />
        );
      },
    }),
    [selectedItems, toggleSelection, getItemId]
  );

  const truncateCell = (cell: React.ReactNode) => <TruncatedCell cell={cell} />;

  const wrappedColumns = useMemo(
    () =>
      columns.map(col => ({
        ...col,
        cell: col.cell
          ? (cellCtx: any) =>
              typeof col.cell === "function" ? truncateCell(col.cell(cellCtx)) : truncateCell(cellCtx.getValue())
          : (cellCtx: any) => truncateCell(cellCtx.getValue()),
      })),
    [columns]
  );

  const allColumns = useMemo(() => [selectColumn, ...wrappedColumns], [selectColumn, wrappedColumns]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="xl" showCloseButton className="flex flex-col max-h-[85vh]">
      <Dialog.Header className="flex-shrink-0">
        <Dialog.Title>{title}</Dialog.Title>
        <Dialog.Description>{description}</Dialog.Description>
        <InputGroup
          control={controlProps => (
            <Input
              {...controlProps}
              type="search"
              placeholder={searchPlaceholder}
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
            />
          )}
          prepend={<SearchIcon />}
        />
      </Dialog.Header>
      <Dialog.Content className="flex flex-1 flex-col gap-4 min-h-0 overflow-auto p-4">
        <DataTable<TRecord, unknown>
          columns={allColumns}
          data={data}
          loading={loading}
          pageCount={totalPages}
          rowCount={totalRecords}
          pagination={pagination}
          onPaginationChange={setPagination}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          onRowClick={row => toggleSelection(row.original)}
        />
      </Dialog.Content>
      <Dialog.Footer className="flex-shrink-0">
        <div className="flex justify-between items-center w-full">
          {secondaryAction && (
            <Button type="button" variant="secondary" onClick={secondaryAction.onClick}>
              {secondaryAction.label}
            </Button>
          )}
          <div className="flex gap-2">
            <Dialog.Close
              render={
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              }
            />
            <Button type="button" onClick={handleSelect} disabled={selectedItems.length === 0}>
              Select{multiSelect ? ` (${selectedItems.length})` : ""}
            </Button>
          </div>
        </div>
      </Dialog.Footer>
    </Dialog>
  );
}

export const RecordSelectionDialog = RecordSelectionDialogInner as <TRecord>(
  props: RecordSelectionDialogProps<TRecord>
) => React.ReactElement;
